import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import WandIcon from "../../../../../../../../assets/Fibo/icons/WandIcon";
import { UploadIcon } from "../../../../../../../../assets/images/icons/UploadIcon";
import { UploadMaskIcon } from "../../../../../../../../assets/images/icons/UploadMaskIcon";
import BriaAccordion from "../../../../../../../../components/common/BriaAccordion/BriaAccordion";
import BriaIconButton from "../../../../../../../../components/common/BriaIconButton/BriaIconButton";
import ImageUploader from "../../../../../../../../components/common/ImageUploader/ImageUploader";
import StoppableButton from "../../../../../../../../components/common/StoppableButton/StoppableButton";
import { useAppStore } from "../../../../../../../../hooks/useStores";
import { uploadFileToS3 } from "../../../../../../../../utils/uploadFileToS3";
import { ActiveTab } from "../../VideoEraserPopup";
import styles from "../VideoEraserPopupConfig.module.scss";

type VideoEraserMaskUploadProps = {
	activeTab: ActiveTab | undefined;
	setActiveTab: (tab: ActiveTab | undefined) => void;
};

const VideoEraserMaskUpload = ({ activeTab, setActiveTab }: VideoEraserMaskUploadProps) => {
	const { t } = useTranslation("video_editing", { keyPrefix: "lab.config.tools.erase.popup.config.upload" });
	const { videoGenerationStore, videoEditingStore } = useAppStore();
	const [loading, setLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined);

	const handleUploadMask = async (e: ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		try {
			setLoading(true);
			const maskUrl = await uploadFileToS3(file, "video", setLoadingProgress);
			videoGenerationStore.handleSegmentConfigChange("mask_url", maskUrl);
		} catch (error: any) {
			throw new Error(`Error uploading mask: ${error?.message || error?.toString()}`);
		} finally {
			setLoadingProgress(undefined);
			setLoading(false);
		}
	};

	const handlePreviewMask = async () => {
		await videoGenerationStore.previewMask();
		videoEditingStore.handleConfigChange("tool_config", {
			erase: { mask: videoGenerationStore.mask_url },
		});
	};

	return (
		<BriaAccordion
			expanded={activeTab === "upload"}
			onChange={(_, expanded: boolean) => setActiveTab(expanded ? "upload" : undefined)}
			accordionTitle={
				<Box className={styles.accordionTitleContainer}>
					<WandIcon className={styles.icon} />
					<Typography className={styles.accordionTitleText}>{t("label")}</Typography>
				</Box>
			}
			titleClass={styles.accordionTitle}
			useSecondaryIcons
		>
			<Box className={styles.content}>
				<ImageUploader
					src={videoGenerationStore.segmentConfig.mask_url}
					droppable
					className={styles.videoUploader}
					emptyStateClassName={styles.imageUploaderEmptyState}
					hideSupportElement
					showIcon
					smallIcon
					inputProps={{ accept: "video/*" }}
					dropOverlayProps={{
						icon: <UploadIcon />,
						text: t("mediaUploader.dropOverlayText"),
					}}
					description={
						<Box className={styles.videoUploaderDescriptionContainer}>
							<Typography className={styles.videoUploaderTitle}>{t("mediaUploader.title")}</Typography>
							<Typography className={styles.videoUploaderDescription}>
								{t("mediaUploader.dragAndDropText")}
							</Typography>
						</Box>
					}
					icon={<UploadIcon size={10} />}
					showTitle
					itemTitle={t("mediaUploader.title")}
					itemDescription={t("mediaUploader.description")}
					customOverlay={({ handleUploadClick }) => (
						<>
							<BriaIconButton
								square
								className={styles.customOverlayButton}
								onClick={handleUploadClick}
								title={t("label")}
							>
								{<UploadMaskIcon size={10} />}
							</BriaIconButton>
						</>
					)}
					customOverlayClassName={styles.customOverlay}
					onUpload={handleUploadMask}
					loading={loading}
					loadingProgress={loadingProgress}
				/>

				<StoppableButton
					condition={videoGenerationStore.isGeneratingMask}
					onStopButtonClick={() => videoGenerationStore.abortVideoSegmentation()}
					onButtonClick={handlePreviewMask}
					buttonLabel={t("generateButton")}
					stopButtonLabel={t("stopGenerate")}
					disabled={!videoGenerationStore.segmentConfig.mask_url}
				/>
			</Box>
		</BriaAccordion>
	);
};

export default observer(VideoEraserMaskUpload);
